import { Controller } from "@hotwired/stimulus"

// Handles the private-beta waitlist form on the marketing home page.
//
// The form posts into a hidden iframe (see the `target` on the <form>) rather
// than via fetch, so it works against any external endpoint — a Google Apps
// Script Web App bound to the signups sheet, a Google Form, etc. — without
// needing CORS headers on the response. We can't read the cross-origin
// response, so we confirm success optimistically once the submit fires and the
// iframe finishes loading.
export default class extends Controller {
  static targets = ["form", "email", "state", "stateStep", "submit", "success", "error"]

  connect() {
    this.step = "email"
  }

  // Two-step: the first tap validates the email and reveals the state dropdown;
  // the second tap validates the state and submits everything into the iframe.
  submit(event) {
    if (this.step === "email") {
      event.preventDefault()
      const email = this.emailTarget.value.trim()
      if (!this.validEmail(email)) {
        this.showError("Please enter a valid email address.")
        this.emailTarget.focus()
        return
      }
      this.hideError()
      this.revealState()
      return
    }

    // step === "state": require a selection before the real submit.
    if (this.hasStateTarget && !this.stateTarget.value) {
      event.preventDefault()
      this.showError("Please tell us where you live.")
      this.stateTarget.focus()
      return
    }

    // Let the native submit continue into the hidden iframe.
    this.hideError()
    this.submitTarget.disabled = true
    this.submitTarget.textContent = "Joining…"

    // The iframe load fires once the endpoint has received the row.
    this.iframe.addEventListener("load", () => this.confirm(), { once: true })

    // Safety net: confirm even if the iframe's load event never arrives
    // (some browsers suppress it for opaque cross-origin responses).
    this.confirmTimeout = setTimeout(() => this.confirm(), 1500)
  }

  revealState() {
    this.step = "state"
    if (this.hasStateStepTarget) this.stateStepTarget.classList.remove("hidden")
    if (this.hasStateTarget) this.stateTarget.focus()
  }

  confirm() {
    if (this.confirmed) return
    this.confirmed = true
    clearTimeout(this.confirmTimeout)
    this.formTarget.classList.add("hidden")
    this.successTarget.classList.remove("hidden")
    this.successTarget.focus()
  }

  validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.add("hidden")
  }

  get iframe() {
    return this.element.querySelector("iframe")
  }
}
